import type { DealApprovalGate, DealOrderStep } from "./crmDealApprovalService";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ClientType } from "@/interfaces";
import { message } from "antd";
import { useMemo } from "react";
import api from "./api";

export enum CrmDealType {
  NEW_BUSINESS = "newBusiness",
  RENEWAL = "renewal",
  // with a vendor and one of its users
  VENDOR = "vendor",
}

/** What a deal is with — its CRM company, a client (renewal) or a vendor — as a tag. */
export const dealPartyTag = (t?: CrmDealType | string | null) =>
  t === CrmDealType.RENEWAL
    ? { label: "Client", color: "green" }
    : t === CrmDealType.VENDOR
      ? { label: "Vendor", color: "purple" }
      : { label: "Company", color: "geekblue" };

export const CRM_STAGE_LEVEL = {
  OPPORTUNITY: 1,
  PROPOSAL: 2,
  NEGOTIATIONS: 3,
  LEGAL: 4,
  CLOSED_WON: 5,
  EXECUTION: 6,
  CLOSED_LOST: 7,
  COMPLETED: 8,
} as const;

export interface CrmStage {
  id: string;
  stageName: string;
  stageLevel: number;
  /** Deals create their order at this stage (Default: Legal) */
  createsOrder?: boolean;
  /** The terminal lost stage (Default: Closed Lost) */
  isLost?: boolean;
  /** Fixed Default stage (vs a stage added for the company) */
  isDefault?: boolean;
  /** Order among the stages (Default stage N = N×10, Closed Lost last) */
  position?: number;
}

/** A stage's order (Default N×10 when the server didn't send one; Completed, then Closed Lost last). */
const positionOf = (s: CrmStage) =>
  s.position ?? (s.isLost ? 1_000_000 : s.stageLevel === CRM_STAGE_LEVEL.COMPLETED ? 999_999 : s.stageLevel * 10);

/** The Default stages (global crm_stages); a company may have its own sequence instead. */
export const DEFAULT_CRM_STAGES: CrmStage[] = [
  { id: "default-1", stageLevel: 1, stageName: "Opportunity" },
  { id: "default-2", stageLevel: 2, stageName: "Proposal" },
  { id: "default-3", stageLevel: 3, stageName: "Negotiations" },
  { id: "default-4", stageLevel: 4, stageName: "Legal", createsOrder: true },
  { id: "default-5", stageLevel: 5, stageName: "Closed Won" },
  { id: "default-6", stageLevel: 6, stageName: "Execution" },
  { id: "default-8", stageLevel: 8, stageName: "Completed" },
  { id: "default-7", stageLevel: 7, stageName: "Closed Lost", isLost: true },
];

/**
 * A company's deal pipeline — mirrors server/src/utils/crmPipelineUtil.ts: the fixed Default stages
 * plus stages added for the company, ordered by position (stage levels are ids, so "next" is the
 * next stage in that order). start = Opportunity; order = Legal (Create Order, no manual move on);
 * won = Closed Won (reached when the order is fully approved; leaving it needs that order);
 * lost = Closed Lost; completed = Completed (last, reached only when the deal's order is closed —
 * never a move target). A deal's own next/previous stage comes from the server (deal.nextStageLevel),
 * since deals skip stages added after they were created.
 */
export interface CrmPipeline {
  stages: CrmStage[];
  /** Forward (non-lost) levels in order, Completed last */
  forwardLevels: number[];
  startLevel: number;
  /** The last stage a deal can be moved to (Execution, or a stage added after it) — Completed comes after it */
  lastForwardLevel: number;
  orderLevel: number | null;
  wonLevel: number | null;
  lostLevel: number | null;
  /** Completed (null when the server doesn't have the stage yet) */
  completedLevel: number | null;
}

export const buildCrmPipeline = (stages: CrmStage[]): CrmPipeline => {
  const sorted = [...stages].sort((a, b) => positionOf(a) - positionOf(b));
  const forwardLevels = sorted.filter((s) => !s.isLost).map((s) => s.stageLevel);
  const completedLevel = forwardLevels.includes(CRM_STAGE_LEVEL.COMPLETED) ? CRM_STAGE_LEVEL.COMPLETED : null;
  // The stages a deal moves through (Completed is reached only by closing the deal's order)
  const movable = forwardLevels.filter((l) => l !== completedLevel);
  const orderLevel = sorted.find((s) => s.createsOrder && !s.isLost)?.stageLevel ?? null;
  const afterOrder = orderLevel !== null ? movable[movable.indexOf(orderLevel) + 1] : undefined;
  return {
    stages: sorted,
    forwardLevels,
    startLevel: movable[0] ?? 1,
    lastForwardLevel: movable[movable.length - 1] ?? 1,
    orderLevel,
    wonLevel: afterOrder ?? null,
    lostLevel: sorted.find((s) => s.isLost)?.stageLevel ?? null,
    completedLevel,
  };
};

/** A stage's place in the pipeline's order (-1 = not a forward stage of it). */
export const stageIndex = (p: CrmPipeline, level: number | null | undefined) => (level == null ? -1 : p.forwardLevels.indexOf(level));
export const nextStageOf = (p: CrmPipeline, level: number) => {
  const i = stageIndex(p, level);
  const next = i === -1 ? null : p.forwardLevels[i + 1] ?? null;
  return next === p.completedLevel ? null : next;
};

export const stageNameOf = (p: CrmPipeline, level: number | null | undefined) =>
  level == null ? "" : p.stages.find((s) => s.stageLevel === level)?.stageName ?? `Stage ${level}`;
export const isLostStage = (p: CrmPipeline, level: number) => p.lostLevel !== null && level === p.lostLevel;
/** Before the deal is won: lost / delete / stale warnings apply here. */
export const isPreWinStage = (p: CrmPipeline, level: number) => {
  if (isLostStage(p, level)) return false;
  const i = stageIndex(p, level);
  if (p.wonLevel !== null) return i < stageIndex(p, p.wonLevel);
  if (p.orderLevel !== null) return i <= stageIndex(p, p.orderLevel);
  return i < stageIndex(p, p.lastForwardLevel);
};
/** Won or after (Default: Closed Won, Execution, Completed). */
export const isWonStage = (p: CrmPipeline, level: number) =>
  !isLostStage(p, level) && p.wonLevel !== null && stageIndex(p, level) >= stageIndex(p, p.wonLevel);
/** New meetings before the order stage; edits up to it (Default: add up to Negotiations, edit up to Legal). */
export const canAddMeetingAtStage = (p: CrmPipeline, level: number) =>
  !isLostStage(p, level) && (p.orderLevel !== null ? stageIndex(p, level) < stageIndex(p, p.orderLevel) : isPreWinStage(p, level));
export const canEditMeetingAtStage = (p: CrmPipeline, level: number) =>
  !isLostStage(p, level) && (p.orderLevel !== null ? stageIndex(p, level) <= stageIndex(p, p.orderLevel) : isPreWinStage(p, level));
/**
 * Stages the deal can only leave while its order is fully approved: from the stage the approved
 * order moved it to (the stage after Legal) up to Closed Won (Default: just Closed Won).
 */
/** A deal with a vendor treated as a vendor: the vendor buys its order itself (no client is made from it). */
export const vendorIsBuyer = (deal: { dealType?: string | null; vendorAs?: string | null }) => deal.dealType === CrmDealType.VENDOR && deal.vendorAs !== "client";

/** A deal that made a client / vendor at Legal instead of an order: the order-gated stages don't wait for one. */
export const takesNoOrder = (deal: { legalAction?: string | null }) =>
  deal.legalAction === "client" || deal.legalAction === "client_po" || deal.legalAction === "vendor";

export const needsApprovedOrderStage = (p: CrmPipeline, level: number) => {
  if (p.wonLevel === null) return false;
  const i = stageIndex(p, level);
  return i >= stageIndex(p, p.wonLevel) && i <= stageIndex(p, 5);
};

/** Stages a template step can gate: every forward stage but the first, the last and Completed. */
export const gatedStageLevels = (p: CrmPipeline) =>
  p.forwardLevels.filter((l) => l !== p.startLevel && l !== p.lastForwardLevel && l !== p.completedLevel);

export interface CrmDealStageHistoryEntry {
  id: string;
  fromStageLevel: number | null;
  toStageLevel: number;
  note: string | null;
  changedAt: string;
  changedBy?: { id: string; firstName: string; lastName: string } | null;
}

export interface CrmDeal {
  // Integer, not uuid — shown as the deal's ID in the deals table.
  id: number;
  dealType: CrmDealType;
  dealName: string;
  dealAmount: number;
  dealCurrency: string;
  dealStage: number;
  /** The deal's own next / previous stage (deals skip stages added after they were created); null at the ends */
  nextStageLevel?: number | null;
  prevStageLevel?: number | null;
  /** Deal page only: the stages this deal goes through, in order */
  stageLevels?: number[];
  stageEnteredAt: string;
  ownerId: string;
  owner?: { id: string; firstName: string; lastName: string; email: string };
  companyId: string;
  // Nullable — only set once this deal's crmCompany converts to a real Client, which happens
  // on "Create Order" from Legal (see crmUtil.prepareDealForOrder).
  clientId: string | null;
  client?: {
    id: string;
    companyName: string;
    clientLegalName?: string | null;
    domain?: string;
    country?: string;
    phone?: string;
    clientType?: string | null;
    industry?: string | null;
    paymentTerm?: string | null;
    approvalStatus: "Draft" | "Pending" | "Approved" | "Rejected";
    isActive: boolean;
    billable: boolean;
    createdAt: string;
    updatedAt: string;
    users?: Array<{ id: string; firstName: string; lastName: string; email: string }>;
  } | null;
  clientUserId: string | null;
  clientUser?: { id: string; firstName: string; lastName: string; email: string } | null;
  crmCompanyId: string;
  crmCompany?: { id: string; companyName: string; clientId: string | null };
  crmContactId: string;
  crmContact?: { id: string; name: string; email: string; phone: string; location: string | null; clientUserId: string | null };
  orderId: number | null;
  // a vendor deal: the vendor and the vendor user it is with, treated as a vendor (buys itself) or a client
  vendorId?: string | null;
  vendorUserId?: string | null;
  vendorAs?: "vendor" | "client" | null;
  eventName: string | null;
  meetingType: string | null;
  callType: string | null;
  industry: string | null;
  categoryId: string | null;
  category?: { id: string; categoryName: string } | null;
  /** Company approval template status (Super Admin → Project Templates); null when none applies. */
  approvalGate?: DealApprovalGate | null;
  /** GET /crm/deals/:id only: Create Order step with the order's L1–L4 approval progress */
  orderProgress?: DealOrderStep | null;
  /** GET /crm/deals: the linked order is (back) in its L1–L4 approvals */
  orderApprovalPending?: boolean;
  /** GET /crm/deals/:id: the deal's company (its invoicing tool picks the client / vendor form's fields) */
  company?: { id: string; companyName: string; invoicingAccounting?: string | null } | null;
  /** GET /crm/deals/:id: the client Create Client made at Legal and its finance approval (Generate PO waits for it) */
  legalClient?: { id: string; companyName: string; approvalStatus: "Draft" | "Pending" | "Approved" | "Rejected" } | null;
  /** GET /crm/deals/:id: the vendor Create Vendor made at Legal and its finance approval */
  legalVendor?: { id: string; vendorName: string; approvalStatus: "Pending" | "Approved" | "Rejected" } | null;
  /** GET /crm/deals/:id: the CRM company's details still missing (Complete Company Details comes first at Legal) */
  companyDetailsMissing?: string[];
  /** GET /crm/deals/:id: the POs kept with the deal (Generate PO) */
  purchaseOrders?: DealPoSummary[];
  /** Legal's one create (Create Order / Client / Vendor) and the id of what it created */
  legalAction?: "order" | "client" | "client_po" | "vendor" | null;
  legalActionRef?: string | null;
  // Planned close date (editable); closedDate is when it actually reached Closed Won.
  expectedCloseDate: string | null;
  closedDate: string | null;
  createdAt: string;
  updatedAt: string;
  stageHistory?: CrmDealStageHistoryEntry[];
}

export interface GetDealsParams {
  page?: number;
  limit?: number;
  search?: string;
  dealStage?: number;
  dealType?: CrmDealType;
  companyId?: string;
  ownerId?: string;
  // Deal creation date range (inclusive), as ISO timestamps.
  createdFrom?: string;
  createdTo?: string;
  // Admin whose NetSuite PL owner(s) the deal creators must share.
  plAdminId?: string;
  // Last activity (deal updatedAt) date range (inclusive), as ISO timestamps.
  lastActivityFrom?: string;
  lastActivityTo?: string;
  // Close date (Closed Won or Closed Lost) range (inclusive), as ISO timestamps.
  closedFrom?: string;
  closedTo?: string;
}

export interface CrmDealOwner {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface CrmDealListResponse {
  data: CrmDeal[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateDealRequest {
  companyId: string;
  // New Business starts from an existing CrmContact (its CrmCompany is derived server-side, never
  // passed separately); Renewal sends the Client and one of its client users instead.
  crmContactId?: string;
  clientId?: string;
  clientUserId?: string;
  // a vendor deal: one of the company's approved vendors and one of its users, treated as a vendor or a client
  vendorId?: string;
  vendorUserId?: string;
  vendorAs?: "vendor" | "client";
  dealName: string;
  industry: string;
  categoryId: string;
  dealType: CrmDealType;
  // ISO date string. Deal owner isn't sent — the server always sets it to the logged-in user.
  expectedCloseDate: string;
  dealCurrency: string;
  dealAmount: number;
  eventName?: string;
  meetingType?: string;
  callType?: string;
  // "Clone Deal" on a Closed Lost deal — the new deal starts at Opportunity.
  clonedFromDealId?: number;
}

export interface OrderPrefillData {
  dealId: number;
  companyId: string;
  // the buyer: a client — or, for a deal with a vendor treated as a vendor, that vendor (and clientId null)
  clientId: string | null;
  buyerVendorId?: string | null;
  companyName: string;
  clientLegalName: string | null;
  clientType: string | null;
  industry: string | null;
  paymentTerms: string | null;
  clientCurrency: string;
  campaignName: string;
  totalOrderValue: number;
  // null when the contact hasn't been materialized into a real client user yet — the order form
  // then falls back to the plain contactName/contactEmail/contactPhone fields below.
  contactUserId: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
}

/** A company's deal stages (its own sequence, or the Default ones). */
export const useGetCrmStages = (companyId?: string | null) => {
  return useQuery<{ data: CrmStage[]; custom: boolean }>({
    queryKey: ["crmStages", companyId ?? "default"],
    queryFn: async () => {
      const response = await api.get("/crm/stages", { params: companyId ? { companyId } : undefined });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/** Super Admin → Company → CRM tab: the company's deal stages and whether only renames are allowed. */
export interface CompanyCrmStages {
  /** All stages in order: the fixed Default ones and the ones added for the company */
  stages: (CrmStage & { inUse?: boolean })[];
  /** No CRM = no deal stages */
  crmEnabled: boolean;
  /** No OMS = Legal doesn't create orders */
  omsEnabled: boolean;
  /** Stages can be added only with a Project Management tool (Teamwork or In House) */
  projectManaged: boolean;
}

export interface SaveCompanyCrmStagesPayload {
  /** All forward stages in order: Default ones by stageLevel, added ones by name (+ stageLevel when existing) */
  stages: { stageLevel?: number | null; name?: string }[];
}

export const useCompanyCrmStages = (companyId?: string | null) =>
  useQuery({
    queryKey: ["companyCrmStages", companyId],
    queryFn: async () => (await api.get<{ data: CompanyCrmStages }>(`/crm/companies/${companyId}/stages`)).data.data,
    enabled: !!companyId,
  });

export const useSaveCompanyCrmStages = (companyId?: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SaveCompanyCrmStagesPayload) =>
      (await api.put<{ message: string; data: CompanyCrmStages }>(`/crm/companies/${companyId}/stages`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companyCrmStages", companyId] });
      // Board, deal pages and templates read the company's stages
      qc.invalidateQueries({ queryKey: ["crmStages"] });
    },
  });
};

/** The company's pipeline; the Default one while loading. */
export const useCrmPipeline = (companyId?: string | null) => {
  const { data } = useGetCrmStages(companyId);
  return useMemo(() => buildCrmPipeline(data?.data?.length ? data.data : DEFAULT_CRM_STAGES), [data]);
};

export interface CrmDealCategory {
  id: string;
  categoryName: string;
  sortOrder: number;
}

// "Category Type" options for the Create Deal form — a global lookup table seeded via PSQL.
export const useGetCrmDealCategories = (enabled: boolean = true) => {
  return useQuery<{ data: CrmDealCategory[] }>({
    queryKey: ["crmDealCategories"],
    queryFn: async () => {
      const response = await api.get("/crm/deal-categories");
      return response.data;
    },
    enabled,
    staleTime: 60 * 60 * 1000,
  });
};

export const useGetCrmDeals = (params?: GetDealsParams) => {
  return useQuery<CrmDealListResponse>({
    queryKey: ["crmDeals", params],
    queryFn: async () => {
      try {
        const response = await api.get("/crm/deals", { params });
        return response.data;
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        message.error(err?.response?.data?.message || "Failed to fetch deals");
        throw error;
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
};

// Distinct owners of the deals the viewer can see — options for the deals "Owner" filter.
export const useGetCrmDealOwners = (companyId?: string) => {
  return useQuery<{ data: CrmDealOwner[] }>({
    queryKey: ["crmDealOwners", companyId],
    queryFn: async () => {
      const response = await api.get("/crm/deal-owners", { params: { companyId } });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetCrmDealById =(id?: string | number, enabled: boolean = true) => {
  return useQuery<{ data: CrmDeal }>({
    // Normalized to string — callers pass a route-param string (DealDetailsPage) or a numeric
    // deal.id (mutation onSuccess handlers below); without this the two never hash to the same
    // query key and invalidateQueries silently misses this cache entry.
    queryKey: ["crmDeal", String(id)],
    queryFn: async () => {
      try {
        const response = await api.get(`/crm/deals/${id}`);
        return response.data;
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        message.error(err?.response?.data?.message || "Failed to fetch deal details");
        throw error;
      }
    },
    enabled: !!id && enabled,
    staleTime: 60 * 1000,
  });
};

export const useGetCrmDealOrderPrefill = (id?: string | number, enabled: boolean = true) => {
  return useQuery<{ data: OrderPrefillData }>({
    queryKey: ["crmDealOrderPrefill", String(id)],
    queryFn: async () => {
      const response = await api.get(`/crm/deals/${id}/order-prefill`);
      return response.data;
    },
    enabled: !!id && enabled,
    retry: false,
  });
};

// Generate PO: POs kept with the CRM deal (not orders), a header and line items like an order's
export interface DealPoSummary {
  id: string;
  poNumber: string;
  status: string;
  totalValue: number;
  currency: string | null;
  lineItems: number;
  createdAt: string;
}
export interface DealPoLineItem {
  id?: string;
  serviceId: string | null;
  vendorId: string | null;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  estimatedCost: number;
  revenue: number;
  serviceName?: string | null;
  vendorName?: string | null;
  profit?: number;
}
export interface DealPo {
  id: string;
  dealId: number;
  clientId: string;
  poNumber: string;
  campaignName: string | null;
  currency: string | null;
  startDate: string | null;
  endDate: string | null;
  totalValue: number;
  notes: string | null;
  poFileName: string | null;
  hasFile: boolean;
  status: string;
  lineItems: DealPoLineItem[];
}
export type DealPoInput = Omit<DealPo, "id" | "dealId" | "clientId" | "poFileName" | "hasFile" | "status" | "poNumber" | "lineItems"> & {
  poNumber: string | null;
  removeFile?: boolean;
  /** Release PO: saves it released (locked) */
  release?: boolean;
  lineItems: DealPoLineItem[];
};

export const useGetDealPo = (dealId?: string, poId?: string) =>
  useQuery<{ data: DealPo }>({
    queryKey: ["crmDealPo", dealId, poId],
    queryFn: async () => (await api.get(`/crm/deals/${dealId}/purchase-orders/${poId}`)).data,
    enabled: !!dealId && !!poId,
  });

/** Saves a deal PO: creates it (no poId) or updates it; the PO document goes along as a file. */
export const useSaveDealPo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ dealId, poId, po, file }: { dealId: number; poId?: string; po: DealPoInput; file?: File | null }) => {
      const form = new FormData();
      form.append("data", JSON.stringify(po));
      if (file) form.append("document", file);
      const url = poId ? `/crm/deals/${dealId}/purchase-orders/${poId}` : `/crm/deals/${dealId}/purchase-orders`;
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const response = poId
        ? await api.put<{ message: string; data: { id: string; poNumber: string } }>(url, form, config)
        : await api.post<{ message: string; data: { id: string; poNumber: string } }>(url, form, config);
      return response.data;
    },
    onSuccess: (_, { dealId, poId }) => {
      queryClient.invalidateQueries({ queryKey: ["crmDeal", String(dealId)] });
      if (poId) queryClient.invalidateQueries({ queryKey: ["crmDealPo", String(dealId), poId] });
    },
  });
};

export const getDealPoDocumentUrl = async (dealId: number, poId: string) =>
  (await api.get<{ data: { url: string; fileName: string | null } }>(`/crm/deals/${dealId}/purchase-orders/${poId}/document`)).data;

export interface PrepareDealOrderResult {
  dealId: number;
  clientId: string | null;
  // a vendor treated as a vendor buys the order itself (no client)
  buyerVendorId?: string | null;
  clientApprovalStatus: "Draft" | "Pending" | "Approved" | "Rejected";
  // Labels of mandatory client fields still empty; when non-empty, `client` is the full client
  // record so its edit form can be opened.
  missingClientFields: string[];
  client?: ClientType;
}

/** Legal's one create: records the client or vendor the deal's form just created. */
export const useRecordLegalAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action, refId }: { id: number; action: "client" | "client_po" | "vendor"; refId: string }) =>
      (await api.post<{ message: string }>(`/crm/deals/${id}/legal-action`, { action, refId })).data,
    onSuccess: (_, { id }) => queryClient.invalidateQueries({ queryKey: ["crmDeal", String(id)] }),
  });
};


/**
 * "Create Order" step 1 for a Legal deal: sets the deal's company up as a client and checks its
 * mandatory client fields. The order form can be opened once the client is Approved.
 */
export const usePrepareDealForOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post<{ message: string; data: PrepareDealOrderResult }>(
        `/crm/deals/${id}/prepare-order`,
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["crmDeals"] });
      queryClient.invalidateQueries({ queryKey: ["crmDeal", String(id)] });
    },
  });
};

export const useCreateCrmDeal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateDealRequest) => {
      const response = await api.post<{ message: string; data: CrmDeal }>("/crm/deals", payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crmDeals"] });
    },
  });
};

export interface UpdateDealRequest {
  dealName?: string;
  dealAmount?: number;
  dealCurrency?: string;
  eventName?: string;
  meetingType?: string;
  callType?: string;
}

export const useUpdateCrmDeal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: UpdateDealRequest }) => {
      const response = await api.put(`/crm/deals/${id}`, payload);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["crmDeals"] });
      queryClient.invalidateQueries({ queryKey: ["crmDeal", String(variables.id)] });
    },
  });
};

export const useMoveDealToNextStage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post(`/crm/deals/${id}/next-stage`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["crmDeals"] });
      queryClient.invalidateQueries({ queryKey: ["crmDeal", String(id)] });
    },
  });
};

export const useMoveDealToPreviousStage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post(`/crm/deals/${id}/previous-stage`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["crmDeals"] });
      queryClient.invalidateQueries({ queryKey: ["crmDeal", String(id)] });
    },
  });
};

export const useMarkDealClosedLost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: number; note?: string }) => {
      const response = await api.post(`/crm/deals/${id}/close-lost`, { note });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["crmDeals"] });
      queryClient.invalidateQueries({ queryKey: ["crmDeal", String(variables.id)] });
    },
  });
};

export const useDeleteCrmDeal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/crm/deals/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crmDeals"] });
    },
  });
};

// ---------------------------------------------------------------------------
// Deal meetings ("Add Meeting Details") — crm_deal_meetings
// ---------------------------------------------------------------------------

export enum CrmMeetingMode {
  VIRTUAL = "virtual",
  IN_PERSON = "inPerson",
  PHONE = "phone",
}

export enum CrmMeetingStatus {
  SCHEDULED = "scheduled",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}


export interface CrmDealMeeting {
  id: string;
  dealId: number;
  stageLevel: number;
  title: string;
  meetingMode: CrmMeetingMode;
  status: CrmMeetingStatus;
  startTime: string;
  endTime: string;
  timezone: string | null;
  platform: string | null;
  meetingLink: string | null;
  location: string | null;
  internalAttendeeIds: string[];
  internalAttendees: { id: string; name: string }[];
  clientAttendees: string | null;
  agenda: string | null;
  outcome: string | null;
  followUpDate: string | null;
  createdByUserId: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DealMeetingPayload {
  title: string;
  meetingMode: CrmMeetingMode;
  status?: CrmMeetingStatus;
  // ISO timestamps
  startTime: string;
  endTime: string;
  timezone?: string | null;
  platform?: string | null;
  meetingLink?: string | null;
  location?: string | null;
  internalAttendeeIds?: string[];
  clientAttendees?: string | null;
  agenda?: string | null;
  outcome?: string | null;
  // YYYY-MM-DD
  followUpDate?: string | null;
}

export const useGetDealMeetings = (dealId?: number | string, enabled = true) =>
  useQuery<{ data: CrmDealMeeting[] }>({
    queryKey: ["crmDealMeetings", String(dealId)],
    queryFn: async () => {
      const response = await api.get(`/crm/deals/${dealId}/meetings`);
      return response.data;
    },
    enabled: !!dealId && enabled,
  });

const invalidateMeetingQueries = (queryClient: ReturnType<typeof useQueryClient>, dealId: number | string) => {
  queryClient.invalidateQueries({ queryKey: ["crmDealMeetings", String(dealId)] });
  queryClient.invalidateQueries({ queryKey: ["crmMeetings"] });
  // Adding/editing a meeting bumps the deal's last-activity timestamp.
  queryClient.invalidateQueries({ queryKey: ["crmDeals"] });
};

export const useSaveDealMeeting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      dealId,
      meetingId,
      payload,
    }: {
      dealId: number | string;
      meetingId?: string;
      payload: DealMeetingPayload;
    }) => {
      const response = meetingId
        ? await api.put<{ message: string; data: CrmDealMeeting }>(`/crm/deals/${dealId}/meetings/${meetingId}`, payload)
        : await api.post<{ message: string; data: CrmDealMeeting }>(`/crm/deals/${dealId}/meetings`, payload);
      return response.data;
    },
    onSuccess: (_, variables) => invalidateMeetingQueries(queryClient, variables.dealId),
  });
};

export const useDeleteDealMeeting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ dealId, meetingId }: { dealId: number | string; meetingId: string }) => {
      const response = await api.delete(`/crm/deals/${dealId}/meetings/${meetingId}`);
      return response.data;
    },
    onSuccess: (_, variables) => invalidateMeetingQueries(queryClient, variables.dealId),
  });
};

/** Quick views on the Meetings page — each KPI card filters to one. */
export type CrmMeetingView = "upcoming" | "completed" | "awaitingUpdate" | "followUpDue";

export interface CrmMeetingListItem extends CrmDealMeeting {
  deal: {
    id: number;
    dealName: string;
    dealStage: number;
    companyId: string;
    ownerId: string;
    crmContactId: string;
    crmCompany?: { id: string; companyName: string } | null;
    owner?: { id: string; firstName: string; lastName: string; email: string } | null;
  };
}

export interface CrmMeetingKpis {
  total: number;
  upcoming: number;
  completed: number;
  cancelled: number;
  // Still "Scheduled" although the meeting has ended — outcome not recorded yet.
  awaitingUpdate: number;
  // Follow-up date today or earlier, on an open deal.
  followUpDue: number;
}

export interface GetMeetingsParams {
  page?: number;
  limit?: number;
  companyId?: string;
  search?: string;
  status?: CrmMeetingStatus;
  meetingMode?: CrmMeetingMode;
  ownerId?: string;
  attendeeId?: string;
  // ISO timestamps, on the meeting start time
  from?: string;
  to?: string;
  view?: CrmMeetingView;
  // Viewer's local YYYY-MM-DD, for "follow-ups due"
  today?: string;
}

export const useGetCrmMeetings = (params: GetMeetingsParams) =>
  useQuery<{ data: CrmMeetingListItem[]; total: number; page: number; limit: number; kpis: CrmMeetingKpis }>({
    queryKey: ["crmMeetings", params],
    queryFn: async () => {
      const response = await api.get("/crm/meetings", { params });
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
